import { useState } from 'react';
import { MapPin, RotateCcw } from 'lucide-react';
import type { AnalysisResult } from '../domain/models';
import { reverseGeocodeRepresentativeVisits, type GeocodeProgress } from '../services/reverseGeocode';

type PlaceNameResolverProps = {
  result: AnalysisResult;
  onResolved: (result: AnalysisResult) => void;
};

export function PlaceNameResolver({ result, onResolved }: PlaceNameResolverProps) {
  const [progress, setProgress] = useState<GeocodeProgress | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  const resolveNames = async () => {
    setRunning(true);
    setMessage(null);
    try {
      const geocoded = await reverseGeocodeRepresentativeVisits(result, setProgress);
      onResolved(geocoded.result);
      setMessage(`${geocoded.resolvedCount.toLocaleString('ja-JP')} 件の滞在候補に地名を付与しました`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '地名取得に失敗しました');
    } finally {
      setRunning(false);
      setProgress(null);
    }
  };

  return (
    <section className="surface" style={{ padding: 16 }}>
      <div className="section">
        <div className="section-header">
          <div>
            <h2 className="section-title">地名補完</h2>
            <p className="section-note">
              Google のエクスポートには具体的な場所名が入っていない場合があります。代表滞在地点だけを OpenStreetMap Nominatim に問い合わせて、地名を付与します。
            </p>
          </div>
          <button className="button primary" type="button" disabled={running || result.visits.length === 0} onClick={resolveNames}>
            {running ? <RotateCcw size={16} aria-hidden="true" /> : <MapPin size={16} aria-hidden="true" />}
            地名を取得
          </button>
        </div>
        <div className="alert warning">
          地名取得を押すと、代表地点の緯度経度が外部サービスへ送信されます。元の JSON ファイル全体や全点列は送信しません。
        </div>
        {progress ? (
          <div className="section">
            <div className="section-note">{progress.message}</div>
            <div className="progress" aria-label="地名取得進捗">
              <span style={{ width: `${Math.round((progress.current / Math.max(1, progress.total)) * 100)}%` }} />
            </div>
          </div>
        ) : null}
        {message ? <div className="alert">{message}</div> : null}
      </div>
    </section>
  );
}
